import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone, MessageCircle } from "lucide-react";
import Logo from "./Logo";
import { useLang } from "@/context/LanguageContext";
import { PHONE_DISPLAY, PHONE_TEL, openWa } from "@/lib/wa";

export default function Navbar() {
  const { t, toggle, lang } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { to: "/", key: "nav.home" },
    { to: "/services", key: "nav.services" },
    { to: "/tires", key: "nav.tires" },
    { to: "/tpms", key: "nav.tpms" },
    { to: "/riyadh", key: "nav.coverage" },
    { to: "/blog", key: "nav.blog" },
    { to: "/about", key: "nav.about" },
    { to: "/contact", key: "nav.contact" },
  ];

  return (
    <header className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${scrolled || open ? "border-b border-white/10 bg-ink/85 backdrop-blur-xl" : "bg-gradient-to-b from-ink/80 to-transparent"}`}>
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between gap-4 px-5 lg:px-8">
        <Link to="/" data-testid="nav-logo-link" aria-label="MERO home" className="shrink-0">
          <Logo />
        </Link>

        <nav className="hidden items-center gap-6 xl:flex">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.key.replace("nav.", "")}`}
              className={({ isActive }) =>
                `relative py-2 text-[15px] font-semibold transition-colors duration-200 ${isActive ? "text-gold" : "text-zinc-300 hover:text-white"}`
              }
            >
              {({ isActive }) => (
                <>
                  {t(l.key)}
                  {isActive && <motion.span layoutId="nav-underline" className="absolute inset-x-0 -bottom-0.5 h-0.5 rounded-full bg-gold" />}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2.5">
          <a href={PHONE_TEL} data-testid="nav-call-button" className="hidden items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm font-bold text-white transition-colors hover:border-gold/60 hover:text-gold md:inline-flex" dir="ltr">
            <Phone size={15} /> {PHONE_DISPLAY}
          </a>
          <button onClick={toggle} data-testid="lang-toggle-button" className="rounded-full border border-white/15 px-3.5 py-2 font-display text-sm font-bold text-gold transition-colors hover:border-gold/60">
            {lang === "ar" ? "EN" : "عربي"}
          </button>
          <Link to="/order" data-testid="nav-order-cta" className="hidden rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-ink transition-all duration-300 hover:-translate-y-0.5 hover:bg-gold-hover sm:inline-flex">
            {t("cta.order")}
          </Link>
          <button onClick={() => setOpen((v) => !v)} data-testid="nav-mobile-toggle" aria-label="Menu" className="inline-flex rounded-xl border border-white/15 p-2.5 text-white xl:hidden">
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="overflow-hidden border-t border-white/10 bg-ink/95 backdrop-blur-xl xl:hidden"
            data-testid="nav-mobile-drawer"
          >
            <div className="flex flex-col gap-1 px-5 py-4">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  className={({ isActive }) => `rounded-xl px-4 py-3 text-base font-semibold ${isActive ? "bg-gold/10 text-gold" : "text-zinc-200"}`}
                >
                  {t(l.key)}
                </NavLink>
              ))}
              <div className="mt-3 grid grid-cols-2 gap-3">
                <a href={PHONE_TEL} data-testid="nav-mobile-call" className="btn-ghost !px-4 !py-3 text-sm"><Phone size={16} /> {t("cta.call")}</a>
                <button onClick={() => openWa()} data-testid="nav-mobile-wa" className="btn-wa !px-4 !py-3 text-sm"><MessageCircle size={16} /> {t("common.whatsapp")}</button>
              </div>
              <Link to="/order" className="btn-gold mt-2 !py-3 text-center text-base" data-testid="nav-mobile-order">{t("cta.order")}</Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
